/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React, { useState } from 'react';
import { styled } from '@superset-ui/core';
import { AntdCard } from 'src/components';
import { Tooltip } from 'src/components/Tooltip';
import ImageLoader, { BackgroundPosition } from './ImageLoader';
import CertifiedBadge from '../CertifiedBadge';

// import dashboard_icon from 'images/icons/nav_dashboard.svg';
// import chart_icon from 'images/icons/nav_charts.svg';
import { theme } from 'src/themeSchema';
// import { stringify } from 'query-string';

const ActionsWrapper = styled.div`
  width: 64px;
  display: flex;
  justify-content: flex-end;
`;

const StyledCard = styled(AntdCard)`
// width:360px;
  height:87px !important;
  border: 2px solid #EAEAEA !important;
  background:transparent;
  border-radius: ${({ theme }) => theme.gridUnit}px;
  overflow: hidden;

  .ant-card-body {
    height:100%;
    width:100%;
    overflow:hidden;
    display:flex;
    align-items:center;
    padding:0;
    // padding:8px !important;
    // padding-right:6px !important;
  }

  .ant-card-meta{
    height:100%;
    width:100%;
    overflow:hidden;
    display:flex;
    align-items:center;
    padding:8px 10px;

    .ant-card-meta-avatar{
      padding-left: 10px
    }
    .ant-card-meta-detail{
      height:100%;
      flex-grow:1;
      .ant-card-meta-title{
        height:100%;
        position:relative;
        .card-actions{
          position:absolute;
          top:0;
          right:0;
          display:flex;
          align-items:center;
          justify-content:flex-end;
          &:first-child{
            width:30px !important;
          }
          svg{
            width:16px !important;
            height:16px !important;
          }
          a{
            margin-right:8px;
            svg{
              width:18px !important;
              height:18px !important;
            }
          } 
        }

        .title-and-desc{
          margin-left:8px;
          height:100%;
          display:flex;
          flex-direction:column;
          align-items:flex-start;
          justify-content:center;
          .title{
            a{
              font-style: normal;
              // font-weight: bold;
              font-size: 15px;
              line-height: 19px;
              color: #000000;
            }
          }
          .description{
            font-style: normal;
            font-weight: normal;
            font-size: 13px;
            line-height: 16px;
            color: #333333;
          }
        }
        .publish-tag{
          right:0;
          bottom:-8px;
          position:absolute;
          text-align:end;
          .ant-tag{
            padding:0;
            margin:0;
            background: #F4F5F9;
            padding:2px 9px;
            border-radius:8px;
            border:1px solid #EAEAEA;
            font-style: normal;
            font-weight: normal;
            font-size: 11px;
            line-height: 14px;
            color: #376DC8;
            text-transform:capitalize;
          }
        }

      }
    }
  }
  &:hover {
    box-shadow: 8px 8px 16px 0px rgba(0, 0, 0, 0.24);
    transition: box-shadow ${({ theme }) => theme.transitionTiming}s ease-in-out;

    .gradient-container:after {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
    }
    &:hover {
      box-shadow: 8px 8px 28px 0px ${theme.colors.grayscale.light1};
      transition: box-shadow ${theme.transitionTiming}s ease-in-out;

      .cover-footer {
        transform: translateY(0);
      }
    }
  }
`;

const Cover = styled.div`
  height: 264px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscale.light2};
  overflow: hidden;

  .cover-footer {
    transform: translateY(${({ theme }) => theme.gridUnit * 9}px);
    transition: ${({ theme }) => theme.transitionTiming}s ease-out;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;

  .card-actions {
    margin-left: auto;
    align-self: flex-end;
    padding-left: ${({ theme }) => theme.gridUnit}px;
    span[role='img'] {
      display: flex;
      align-items: center;
    }
  }
`;

const TitleLink = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  & a {
    color: ${({ theme }) => theme.colors.grayscale.dark1} !important;
  }
`;

const TitleRight = styled.span`
  position: absolute;
  right: -1px;
  bottom: ${({ theme }) => theme.gridUnit}px;
`;

const CoverFooter = styled.div`
  display: flex;
  flex-wrap: nowrap;
  position: relative;
  top: -${({ theme }) => theme.gridUnit * 9}px;
  padding: 0 8px;
`;

const CoverFooterLeft = styled.div`
  flex: 1;
  overflow: hidden;
`;

const CoverFooterRight = styled.div`
  align-self: flex-end;
  margin-left: auto;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// const ThinSkeleton = styled(Skeleton)`
//   h3 {
//     margin: ${({ theme }) => theme.gridUnit}px 0;
//   }

//   ul {
//     margin-bottom: 0;
//   }
// `;

// const paragraphConfig = { rows: 1, width: 150 };

interface LinkProps {
  to: string;
}

const AnchorLink: React.FC<LinkProps> = ({ to, children }) => (
  <a href={to}>{children}</a>
);

interface CardProps {
  title?: React.ReactNode;
  url?: string;
  linkComponent?: React.ComponentType<LinkProps>;
  imgURL?: string;
  imgFallbackURL?: string;
  imgPosition?: BackgroundPosition;
  description: string;
  loading?: boolean;
  titleRight?: React.ReactNode;
  coverLeft?: React.ReactNode;
  coverRight?: React.ReactNode;
  actions?: React.ReactNode | null;
  rows?: number | string;
  avatar?: React.ReactElement | null;
  cover?: React.ReactNode | null;
  certifiedBy?: string;
  certificationDetails?: string;
}

function ListViewCard({
  title,
  url,
  linkComponent,
  titleRight,
  imgURL,
  imgFallbackURL,
  description,
  coverLeft,
  coverRight,
  actions,
  avatar,
  loading,
  imgPosition = 'top',
  cover,
  certifiedBy,
  certificationDetails,
}: CardProps) {
  const Link = url && linkComponent ? linkComponent : AnchorLink;
  const [titleHoverState,setTitleHoverState] = useState(false);
  

  const showHoverTitle = ()=>{
    if(titleHoverState){
      return;
    }
    setTitleHoverState(true);
    setTimeout(()=>{
      setTitleHoverState(false);
    },700)
  }

  return (
    <StyledCard
      data-test="styled-card"
      cover={
        cover || (
          <Cover>
            <Link to={url!}>
              <div className="gradient-container">
                <ImageLoader
                  src={imgURL || ''}
                  fallback={imgFallbackURL || ''}
                  isLoading={loading}
                  position={imgPosition}
                />
              </div>
            </Link>
            <CoverFooter className="cover-footer">
              {!loading && coverLeft && (
                <CoverFooterLeft>{coverLeft}</CoverFooterLeft>
              )}
              {!loading && coverRight && (
                <CoverFooterRight>{coverRight}</CoverFooterRight>
              )}
            </CoverFooter>
          </Cover>
        )
      }
    >
      {loading && (
        <AntdCard.Meta
          title={
            <>
              <div className="card-actions" data-test="card-actions" style={{borderRadius:'4px',width:'48px',height:'20px',background:'#f3f3f3'}}>
              </div>
              <div className="title-and-desc hello">
                <div className="title" style={{borderRadius:'4px',width:'220px',height:'24px', marginBottom:'4px',background:'#f3f3f3'}} onMouseEnter={showHoverTitle}></div>
                <div className="description" style={{borderRadius:'4px',width:'160px',height:'16px',background:'#f3f3f3'}}></div>
              </div>
              <div className="publish-tag" style={{borderRadius:'4px',width:'64px',height:'28px',background:'#f3f3f3'}}>
              </div>
            </>
          }
        />
      )}
      {!loading && (
        <AntdCard.Meta
          title={
            <TitleContainer>
              <Tooltip title={title}>
                <TitleLink>
                  <Link to={url!}>
                    {certifiedBy && (
                      <>
                        <CertifiedBadge
                          certifiedBy={certifiedBy}
                          details={certificationDetails}
                        />{' '}
                      </>
                    )}
                    {title}
                  </Link>
                </TitleLink>
              </Tooltip>
              {titleRight && <TitleRight>{titleRight}</TitleRight>}
              <div className="card-actions" data-test="card-actions">
                {actions}
              </div>
              <div className="title-and-desc hello">
                <div className="title" onMouseEnter={showHoverTitle}>
                  <Tooltip
                    id="card-title-tooltip"
                    title={title}
                    placement="topLeft"
                  >
                    <Link to={url!}>{typeof(title) === 'string' ? (title.length>32?title.substr(0,32)+'...':title):title}</Link>
                  </Tooltip></div>
                <div className="description">{description}</div>
              </div>
              <div className="publish-tag">
                {titleRight}
              </div>
            </TitleContainer>
          }
          // avatar={avatar || null}
          avatar={null}
        />
      )}
    </StyledCard>
  );
}

ListViewCard.Actions = ActionsWrapper;

export default ListViewCard;
